// =========================================================================
// ReportChatCard — a generated report in the assistant rail.
//
// Port of the demo's `reportProgress` (demo line 2673) and `reportCard`
// (2675): a "Generating · <report>" box whose steps light one at a time —
// each a data read and the count it found — then the report's card with Open,
// Download and (for the email) Create Outlook draft.
//
// The steps tick at the demo's pace, 380 ms each and 520 ms for the last. They
// play once per answer: a module-level set remembers which runs have played,
// so scrolling the thread or switching back to it shows them finished rather
// than replaying. Reduced motion shows them finished straight away.
//
// Download goes to the report page with `?download=1`; the page downloads
// once its document has rendered (`modules/reports/ReportActions.tsx`).
//
// Fork-owned (zone C). UI layer.
// =========================================================================

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, FileText, Mail } from 'lucide-react';
import type { ChatMessagePart } from '../shared/chat-types';
import { OemLogo } from '../shared/docs/OemLogo';
import { useToast } from '../shared/toast/ToastContext';

type ReportPart = Extract<ChatMessagePart, { type: 'report' }>;

/** Demo 2674. Milliseconds per step, and for the last one. */
const STEP_MS = 380;
const LAST_STEP_MS = 520;

/** Runs whose steps have finished playing, by message id. */
const PLAYED = new Set<number>();

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]';

export function ReportChatCard({ part }: { part: ReportPart }) {
  const total = part.steps.length;
  const [done, setDone] = useState<number>(() =>
    PLAYED.has(part.runId) || prefersReducedMotion() ? total : 0,
  );
  const cardRef = useRef<HTMLDivElement>(null);
  const { show: toast } = useToast();

  useEffect(() => {
    if (done >= total) {
      PLAYED.add(part.runId);
      return;
    }
    const handle = window.setTimeout(
      () => setDone((n) => n + 1),
      done === total - 1 ? LAST_STEP_MS : STEP_MS,
    );
    return () => window.clearTimeout(handle);
  }, [done, part.runId, total]);

  const finished = done >= total;

  useEffect(() => {
    if (finished) cardRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [finished]);

  return (
    <div className="space-y-2">
      <div
        className="rounded-lg border px-3 py-2"
        style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-surface-soft)' }}
        role="status"
        aria-live="polite"
        aria-label={finished ? `Generated · ${part.label}` : `Generating · ${part.label}`}
      >
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">
          {finished ? 'Generated' : 'Generating'} · {part.label}
        </div>
        <ol className="space-y-0.5">
          {part.steps.map((step, i) => {
            const state = i < done ? 'done' : i === done ? 'run' : 'idle';
            return (
              <li
                key={step.label}
                className="grid grid-cols-[12px_minmax(0,1fr)_auto] items-center gap-2 text-[11.5px] transition-opacity duration-200"
                style={{
                  opacity: state === 'idle' ? 0.35 : 1,
                  color: state === 'idle' ? 'var(--ink-tertiary)' : 'var(--ink-secondary)',
                }}
              >
                <StepDot state={state} />
                <span className="min-w-0 truncate">{step.label}</span>
                <span className="mono text-[10.5px] text-muted">{step.detail}</span>
              </li>
            );
          })}
        </ol>
      </div>

      {finished ? (
        <div
          ref={cardRef}
          className="rounded-lg border px-3 py-2.5 text-[12px]"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-surface)' }}
        >
          <div className="flex items-start gap-2">
            <OemLogo height={14} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <b className="block text-[12.5px] text-zinc-900 dark:text-zinc-100">{part.title}</b>
              <span className="block text-[11px] text-muted">{part.subtitle}</span>
            </div>
            <span
              className="mono flex-shrink-0 rounded px-1.5 py-px text-[10px] font-semibold tracking-[0.06em]"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            >
              {part.format.toUpperCase()}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Link
              to={part.route}
              className={`inline-flex min-h-[28px] items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] font-medium ${FOCUS_RING}`}
              style={{ background: 'var(--accent)', color: 'var(--accent-text)' }}
            >
              <FileText className="h-3 w-3" aria-hidden="true" />
              Open report
            </Link>
            {part.download ? (
              <Link
                to={withDownload(part.route)}
                className={`inline-flex min-h-[28px] items-center gap-1 rounded-md border px-2.5 py-1 text-[11.5px] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--glass-border-strong)', color: 'var(--ink-primary)' }}
              >
                <Download className="h-3 w-3" aria-hidden="true" />
                Download {part.download.toUpperCase()}
              </Link>
            ) : (
              <span className="self-center text-[11px] text-muted">in-app only</span>
            )}
            {part.kind === 'email' ? (
              <button
                type="button"
                onClick={() => toast('In production this creates the draft in Outlook through the connector')}
                className={`inline-flex min-h-[28px] items-center gap-1 rounded-md border px-2.5 py-1 text-[11.5px] ${FOCUS_RING}`}
                style={{ borderColor: 'var(--glass-border-strong)', color: 'var(--ink-primary)' }}
              >
                <Mail className="h-3 w-3" aria-hidden="true" />
                Create Outlook draft
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** `/reports/sos?site=KG` → `/reports/sos?site=KG&download=1`. */
const withDownload = (route: string): string =>
  `${route}${route.includes('?') ? '&' : '?'}download=1`;

/** Demo `.gstep i` — idle ring, pulsing accent while running, a tick when done. */
function StepDot({ state }: { state: 'idle' | 'run' | 'done' }) {
  if (state === 'done') {
    return (
      <span
        className="inline-flex h-3 w-3 items-center justify-center rounded-full"
        style={{ background: 'var(--state-online)' }}
        aria-hidden="true"
      >
        <Check className="h-2 w-2" style={{ color: 'var(--color-zinc-50)' }} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      className={`block h-2 w-2 justify-self-center rounded-full border-[1.5px] ${
        state === 'run' ? 'animate-pulse motion-reduce:animate-none' : ''
      }`}
      style={
        state === 'run'
          ? { borderColor: 'var(--accent)', background: 'var(--accent)' }
          : { borderColor: 'var(--hairline-strong)' }
      }
      aria-hidden="true"
    />
  );
}
